import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import EditNicknameDrawer from '@/components/ui/molecules/EditNicknameDrawer';
import memberPermissionService from '@/services/member-permissions.service';
import { IconShield, IconPlus, IconUserStar, IconEdit, IconMail, IconPencil, IconLoader2, IconUserX, IconLogout } from '@tabler/icons-react';
import { MButton } from '@/components/ui/atoms/MButtons';
import { useFamily } from '@/hooks/useFamily';
import { getMemberInfo } from '@/hooks/useMembers';
import MAvatar from '@/components/ui/atoms/MAvatar';
import MOptionsMenu from '@/components/ui/atoms/MOptionsMenu';
import { relationshipOptionsForAddMember } from '@/types/relationship';
import { canShowInviteCTA } from '@/components/ui/utils/DataTable.utils';
import MemberCard from './MemberCard';
import FamilyAccessSplit from './FamilyAccessSplit';
import FamilyNotesButtonWithBadge from './FamilyNotesButtonWithBadge';
import { useNotesContext } from '@/context/NotesContext';
import { memberNormalizer } from '@/utils/memberNormalizer';
import { setSelectedMember, clearSelectedMember } from '@/store/authSlice';

import { useNavigate } from 'react-router-dom';
import MHeader from '@/components/ui/organisms/MHeader';
import { useMembers } from '@/hooks/useMembers';
import MInputField from '@/components/ui/atoms/MInputField';
import MFab from '@/components/ui/atoms/MFab';
import FooterDrawer from '@/components/ui/atoms/FooterDrawer';

const FamilyProfile: React.FC = () => {
  const dispatch = useDispatch();
  const { updateSelectedMember } = useMembers();
  // --- Inline edit family name states ---
  const [isEditingFamilyName, setIsEditingFamilyName] = useState(false);
  const [familyNameInput, setFamilyNameInput] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  // --- Handler stubs for menu actions ---
  // Get family members from useMembers hook
  const { data: familyMembers = [], familyLoading: isLoading } = useFamily();
  const loggedInMember = getMemberInfo(true);
  const { fetchUnreadCount } = useNotesContext();
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);
  // Only compute registeredMembers if logged-in user is FO
  const registeredMembers = useMemo(
    () =>
      loggedInMember.family_member_type === 'fo'
        ? familyMembers.filter(
            (m: any) => m.registered_member
          )
        : [],
    [familyMembers, loggedInMember]
  );

  const handleExitFamilyDialog = (member: any) => {
  const isSelf = (member.id || member._id) === (loggedInMember.id || loggedInMember._id);
  const isFO = loggedInMember.family_member_type === 'fo';
  let eligibleForOwnership = [];
  if (isSelf && isFO && Array.isArray(familyMembers)) {
    eligibleForOwnership = familyMembers.filter(
      (m: any) =>
        m.registered_member &&
        (m.id || m._id) !== (loggedInMember.id || loggedInMember._id) &&
        m.family_member_type !== 'fo'
    );
  }
  navigate('/profile/family/delete-member', {
    state: {
      action: isSelf ? 'exit' : 'remove',
      targetMember: member,
      eligibleForOwnership,
      familyName: family?.name,
    }
  });
};
  const handleDeleteProfileDialog = (member: any, registeredMembersCount?: boolean) => {
  const isSelf = (member.id || member._id) === (loggedInMember.id || loggedInMember._id);
  let eligibleForOwnership = [];
  if (
    isSelf &&
    loggedInMember.family_member_type === 'fo' &&
    Array.isArray(familyMembers)
  ) {
    eligibleForOwnership = familyMembers.filter(
      (m: any) =>
        m.registered_member &&
        (m.id || m._id) !== (loggedInMember.id || loggedInMember._id) &&
        m.family_member_type !== 'fo'
    );
  }
  navigate('/profile/family/delete-member', {
    state: {
      action: isSelf ? registeredMembersCount ? 'fo-delete-registered' : 'delete' : 'delete-other',
      targetMember: member,
      eligibleForOwnership,
    },
  });
};

const handleRemoveFromFamily = (member: any) => {
  navigate('/profile/family/delete-member', {
    state: {
      action: 'remove',
      targetMember: member
    }
  });
};

const handleDeleteMember = (member: any) => {
  navigate('/profile/family/delete-member', {
    state: {
      action: 'delete-other',
      targetMember: member
    }
  });
};

  const handleInviteMember = (member: any) => {
    navigate('/profile/family/invite-contact-method', { state: { targetMember: member } });
  };
  const handleResendInvite = (member: any) => {
    // Not a delete action, leave as is.
    navigate('/profile/family/invite-contact-method', { state: { targetMember: member } });
  };

  const navigate = useNavigate();
  const memberInfo = getMemberInfo(true);
  const {
    family,
    managedIds,
    familyLoading,
    familyError,
    data: members,
    refetchFamily,
    isFamilyOwner,
    addFamilyMember,
    updateFamilyName,
  } = useFamily();

  const [showOptions, setShowOptions] = useState<string | null>(null);
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  // Nickname edit drawer state
  const [editNicknameMember, setEditNicknameMember] = useState<any | null>(null);
  const [editNicknameLoading, setEditNicknameLoading] = useState(false);

  // State for managed member ids (flipped)
  // Use useMemo instead of useEffect+useState to avoid infinite loop
  const loggedInMemberId = loggedInMember.id || loggedInMember._id;
  const memberManagedIds = useMemo(() => {
    const tempArray: Record<string, string[]> = { [loggedInMemberId]: [] };
    if (managedIds && loggedInMemberId) {
      Object.entries(managedIds).forEach(([key, arr]) => {
        if (Array.isArray(arr) && arr.includes(loggedInMemberId)) {
          tempArray[loggedInMemberId].push(key);
        }
      });
    }
    return tempArray;
  }, [managedIds, loggedInMemberId]);

  // Get the setFilters function from useFamily hook
  const { setFilters } = useFamily();

  const handleEditNickname = (member: any) => {
    setEditNicknameMember(member);
  };

  const handleMemberClick = (member: any) => {
    // Dispatch selectedMember to Redux
    if (member.id === loggedInMember.id) {
      dispatch(clearSelectedMember());
    } else {
      dispatch(setSelectedMember(member));
    }
    
    // Save to localStorage
    localStorage.setItem('selectedMember', JSON.stringify(member));
    
    // Navigate to member detail page
    navigate(`/profile/family/member/${member.id}`);
  };

  const handleSaveNickname = async (nickname: string) => {
    if (!editNicknameMember) return;
    setEditNicknameLoading(true);
    try {
      await memberPermissionService.updateNickname({
        memberId: loggedInMember.id,
        familyId: memberInfo.current_family,
        familyMemberId: editNicknameMember.id || editNicknameMember._id,
        nickname,
      });
      setEditNicknameMember(null);
      // Update the local state first for immediate feedback
      updateSelectedMember({ nickname })(editNicknameMember.id || editNicknameMember._id);
      
      // Reset filters to trigger a refetch of the family members data
      setFilters(prevFilters => ({ ...prevFilters }));
      
      // Also explicitly refetch the family data to ensure everything is in sync
      window.location.reload();
    } catch (e) {
      // Optionally show error toast
      console.error('Failed to update nickname:', e);
    } finally {
      setEditNicknameLoading(false);
    }
  };


  useEffect(() => {
    refetchFamily();
  }, [refetchFamily]);

  if (familyLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <IconLoader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
    <div style={{ paddingBottom: '160px' }}>
      {/* Sticky header section with gradient background */}
      <div 
        style={{
          position: 'sticky',
          top: -1,
          zIndex: 30,
          background: 'linear-gradient(181deg, #FBF6FF 77.96%, #CBBBF4 120.16%)',
          paddingLeft: '24px',
          paddingRight: '24px',
          paddingBottom: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}
      >
      {/* Header section: Name, Description, Pencil icon */}
      <div className="flex items-center w-full mb-[12px] gap-[20px]">
        <>
          <span className="heading-h7-bold truncate flex-1">{family?.name}</span>
          {isFamilyOwner && (
            <IconPencil
              size={20}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setFamilyNameInput(family?.name || '');
                setIsEditingFamilyName(true);
              }}
              data-testid="edit-family-name"
            />
          )}
          <FooterDrawer
            open={isEditingFamilyName}
            onClose={() => setIsEditingFamilyName(false)}
            title="Edit family name"
            showClose
            height='40vh'
          >
            <div style={{ padding: 24 }}>
              <MInputField
                variant="Outlined"
                size="md"
                value={familyNameInput}
                showLabel
                label="Family name"
                isDrawerInput
                onChange={e => setFamilyNameInput(e.target.value)}
                autoFocus
                className="w-full"
                placeholder="Not Answered"
              />
              <MButton
                style={{ marginTop: 56, width: '100%' }}
                size="lg"
                variant="filled"
                disabled={updateLoading || !familyNameInput.trim() || familyNameInput.trim() === family?.name}
                onClick={async () => {
                  if (!familyNameInput.trim() || familyNameInput.trim() === family?.name) return;
                  setUpdateLoading(true);
                  try {
                    await updateFamilyName(familyNameInput.trim());
                    setIsEditingFamilyName(false);
                  } catch (err) {
                    // handle error
                  } finally {
                    setUpdateLoading(false);
                  }
                }}
              >
                Done
              </MButton>
            </div>
          </FooterDrawer>
        </>
      </div>
      <div style={{ marginTop: -12, padding: '0', marginBottom: 24 }}>
      <MHeader
        className="family-header-gradient"
        module="Family"
        memberInfo={memberInfo}
        title=""
        description="View and manage your family group, health records, and member access."
        learnMoreContent={
          <>
            As Family Owner, you can add or remove family members, manage access to other member accounts, and control invitations.<br /><br />
            You can also add family notes from here. These help you capture important health details or reminders. Family notes are shared with all family members.
          </>
        }
        drawerTitle="Learn More"
      />
      </div>

      <div className="flex flex-col mt-[-57px]">
        {/* Action Buttons */}
        <div className="flex flex-row gap-[8px] mb-[10px] mt-[34px]">
          {loggedInMember.family_member_type === 'fo' && (
            <MButton
              variant="outline"
              size="sm"
              state="default"
              style={{ width: '49%' }}
              leftIcon={<IconPlus size={18} />}
              onClick={() => {
                localStorage.removeItem('add_member_form');
                navigate('/profile/family/add-member');
              }}
            >
              <span className="body-base-semibold whitespace-nowrap">Add Member</span>
            </MButton>
          )}
          {loggedInMember.family_member_type === 'fo' && (
            <MButton
              variant="outline"
              size="sm"
              state="default"
              style={{ width: '49%' }}
              leftIcon={<IconShield size={18} />}
              onClick={() => navigate('/profile/family/manage-access')}
            >
              <span className="body-base-semibold whitespace-nowrap">Manage Access</span>
            </MButton>
          )}
          {loggedInMember.family_member_type === 'fm' && (
            <MButton
              variant="outline"
              size="sm"
              state="default"
              style={{ width: '100%' }}
              leftIcon={<IconShield size={18} />}
              onClick={() => navigate('/profile/family/manage-access')}
            >
              <span className="body-base-semibold whitespace-nowrap">Manage Profile Access</span>
            </MButton>
          )}
        </div>
        <div>
          <FamilyNotesButtonWithBadge loggedInMember={loggedInMember} navigate={navigate} />
        </div>
        </div>
      </div>
      {/* End of sticky section */}
      
      {/* Scrollable member list section */}
      <div style={{ paddingLeft: '24px', paddingRight: '24px' }}>
        {/* Member List */}
        {(familyMembers.length === 1) ? (
          <>
            {/* Family Owner Card */}
            {loggedInMember && (
              <div className="flex flex-col gap-3 mt-[24px]">
                <MemberCard
                  avatar={<MAvatar size="xl" type={loggedInMember?.avatar ? 'dp' : 'no-dp'}
                  src={members[0]?.avatar} text={`${loggedInMember?.firstName || ''}${loggedInMember?.lastName ? ' ' + loggedInMember.lastName : ''}`.trim()} showBadge={true} badgeIcon={<IconUserStar size={16} />} />}
                  name={loggedInMember.firstName}
                  isOwner={true}
                  loggedInMember={true}
                  member={loggedInMember}
                  menu={<MOptionsMenu options={(() => {
                    // Owner menu logic (single member)
                    const menuOptions = [
                      {
                        id: 'delete-account',
                        label: 'Delete account',
                        icon: <IconUserX  />,
                        onClick: () => handleDeleteProfileDialog(loggedInMember),
                        className: 'text-red-600',
                      },
                    ];
                    return menuOptions;
                  })()} />}
                />
              </div>
            )}
            
          </>
        ) : (
          loggedInMember.family_member_type === 'fm'
            ? <FamilyAccessSplit members={members} managedIds={managedIds} loggedInMember={loggedInMember} handleExitFamilyDialog={handleExitFamilyDialog} handleDeleteProfileDialog={handleDeleteProfileDialog} handleEditNickname={handleEditNickname} />
            : <div className="flex flex-col gap-3 mt-[24px]">
              {[...members]
                .sort((a, b) => {
                  // Logged-in member first
                  if ((a.id ) === (loggedInMember.id )) return -1;
                  if ((b.id ) === (loggedInMember.id )) return 1;
                  // Owner second
                  if (a.family_member_type === 'fo') return -1;
                  if (b.family_member_type === 'fo') return 1;
                  // Otherwise keep original order
                  return 0;
                })
                .map((memberObj: any) => {
              const member = memberNormalizer(memberObj); 
              const isOwner = member.family_member_type === "fo"
              
              const initials = (member.firstName?.[0] || '?') + (member.lastName?.[0] || '');
              const actions = (() => {
                // Show menu for all members if FO, or for self if FM
                const isSelf = (member.id) === (loggedInMember.id);
                const isOwner = member.family_member_type === 'fo';
                const loggedInOwner = loggedInMember.family_member_type === 'fo';
                const canShowInvite = member.registration_status === 'not_invited' && typeof canShowInviteCTA === 'function' && canShowInviteCTA(member);
                const menuOptions = [];
                  // Invite
                if (loggedInOwner && canShowInvite) {
                  menuOptions.push({
                    id: 'invite',
                    label: 'Send invite',
                    icon: <IconMail />, 
                    onClick: () => handleInviteMember(member),
                    className: '',
                  });
                }
                // Revoke invite
                if (loggedInOwner && member.registration_status === 'invited') {
                  menuOptions.push({
                    id: 'revoke',
                    label: 'Resend invite',
                    icon: <IconMail />, 
                    onClick: () => handleResendInvite(member),
                  });
                }
                // Owner logic
                if (loggedInOwner) {
                  const registeredMembersCount = registeredMembers.length > 1
                  if (isSelf && member.registered_member && registeredMembers.length > 1) {
                    menuOptions.push({
                      id: 'exit',
                      label: 'Leave Family',
                      icon: <IconLogout  />, 
                      onClick: () => handleExitFamilyDialog(member),
                    });
                    menuOptions.push({
                      id: 'delete-profile',
                      label: 'Delete account',
                      icon: <IconUserX  />, 
                      onClick: () => handleDeleteProfileDialog(member, registeredMembersCount),
                      className: 'text-red-600',
                    });
                  } else if (!isSelf && member.registered_member) {
                    menuOptions.push({
                      id: 'remove',
                      label: 'Remove from family',
                      icon: <IconUserX  />, 
                      onClick: () => handleRemoveFromFamily(member),
                      delete: true
                    });
                  } else {
                    menuOptions.push({
                      id: 'delete-profile',
                      label: 'Delete account',
                      icon: <IconUserX  />, 
                      onClick: () => handleDeleteProfileDialog(member),
                      delete: true,
                    });
                  }
                } else if (!isOwner) {
                  if (isSelf) {
                      menuOptions.push({
                        id: 'exit',
                        label: 'Leave Family',
                        icon: <IconLogout  />, 
                        onClick: () => handleExitFamilyDialog(member),
                      });
                      menuOptions.push({
                        id: 'delete-profile',
                        label: 'Delete account',
                        icon: <IconUserX  />, 
                        onClick: () => handleDeleteProfileDialog(member),
                        className: 'text-red-600',
                      });
                  }
                }
                if ((loggedInOwner && !isSelf) || memberManagedIds[loggedInMember.id]?.includes(member.id)) {
                  menuOptions.push({
                    id: 'edit-nickname',
                    label: 'Edit nickname',
                    icon: <IconEdit />, 
                    onClick: () => handleEditNickname(member),
                  });
                }
                return menuOptions.length > 0 ? menuOptions : null;
              })();

            return (
              <div key={member.id} style={{ position: 'relative' }}>
                <MemberCard
                  avatar={<MAvatar
                    size="md"
                    type={member?.avatar ? 'dp' : 'no-dp'}
                    src={member?.avatar}
                    text={`${member?.firstName || ''}${member?.lastName ? ' ' + member.lastName : ''}`.trim()}
                    showBadge={!!isOwner}
                    badgeIcon={isOwner ? <IconUserStar size={8} /> : undefined}
                  />}
                  name={member.nickname || member.firstName}
                  isOwner={isOwner}
                  relationship={
                    (member.id || member._id) === (loggedInMember.id || loggedInMember._id)
                      ? 'You'
                      : loggedInMember.family_member_type === 'fo' && !isOwner
                        ? (typeof member.relationship === 'number'
                            ? (relationshipOptionsForAddMember.find(opt => opt.value === member.relationship)?.label || '')
                            : (typeof member.relationship === 'string' ? member.relationship : ''))
                        : undefined
                  }
                  registered={member.registered_member}
                  invited={member.registration_status === 'invited'}
                  showRegistrationLabels={true}
                  member_type={member.member_type}
                  dob_year={member.dob_year}
                  dob_month={member.dob_month}
                  dob_day={member.dob_day}
                  loggedInMember={(member.id || member._id) === (loggedInMember.id || loggedInMember._id)}
                  member={member}
                  onClick={() => handleMemberClick(member)}
                />
                {actions && (
                  <div
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: '40%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      minWidth: 32,
                      zIndex: 2,
                    }}
                  >
                    <MOptionsMenu options={actions} />
                  </div>
                )}
              </div>
            );
          })}
          </div>
        )}
      </div>
    </div>
    <MFab />
    <EditNicknameDrawer
      open={!!editNicknameMember}
      member={editNicknameMember}
      onClose={() => setEditNicknameMember(null)}
      onDone={handleSaveNickname}
    />
    </>
  );
};

export default FamilyProfile;
